<template>
  <!-- Page Header -->
  <PageHeader 
    title="Gestion des Acheteurs" 
    subtitle="Bloquez ou débloquez des acheteurs" 
    class="mb-0 pr-10" 
  />
  
  <div class="w-full px-2 sm:px-6 py-4">
    <!-- Section Formulaire de blocage -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-black">Bloquer un Acheteur</h2>
      </div>
      
      <form @submit.prevent="handleBlockBuyer" class="space-y-6">
        <!-- Nom de l'acheteur avec autocomplétion -->
        <div class="col-md-6">
          <label class="block text-sm font-medium text-black mb-2">
            Nom de l'acheteur à bloquer
          </label>
          <div class="flex gap-3 items-start">
            <div class="flex-1">
              <BuyerAutocompleteForBlocking v-model="selectedBuyerIds" :multiple="true" ref="buyerAutocompleteRef" />
            </div>
            
            <button 
              type="submit"
              :disabled="selectedBuyerIds.length === 0"
              class="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              <i class="fas fa-ban mr-2"></i>
              Bloquer l'acheteur
            </button>
          </div>
        </div>
      </form>
    </div>
    
    <!-- Section Liste des acheteurs bloqués -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-black">Acheteurs Bloqués</h2>
        <div class="text-sm text-black">
          {{ blockedBuyers.length }} acheteur{{ blockedBuyers.length > 1 ? 's' : '' }} bloqué{{ blockedBuyers.length > 1 ? 's' : '' }}
        </div>
      </div>
      
      <!-- Message si aucun acheteur bloqué -->
      <div v-if="blockedBuyers.length === 0" class="text-center py-12">
        <div class="bg-green-50 border border-green-200 rounded-lg p-6 inline-block">
          <i class="fas fa-check-circle text-green-400 text-4xl mb-4"></i>
          <div class="text-green-800 font-medium mb-2">Aucun acheteur bloqué</div>
          <div class="text-green-600 text-sm">
            Vous n'avez bloqué aucun acheteur pour le moment.
          </div>
        </div>
      </div>
      
      <!-- Tableau des acheteurs bloqués -->
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                Acheteur
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                Date de blocage
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="buyer in blockedBuyers" :key="buyer.id_vendeur_acheteur_disabled" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10">
                    <div class="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                      <i class="fas fa-user text-red-600"></i>
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-medium text-black">
                      {{ buyer.acheteur?.nomSociete || 'N/A' }}
                    </div>
                    <div class="text-sm text-black">
                      {{ buyer.acheteur?.email || `ID: ${buyer.id_acheteur}` }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-black">
                {{ formatDate(buyer.date_disabled) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button 
                  @click="handleUnblockBuyer(buyer)"
                  class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                >
                  <i class="fas fa-unlock mr-1"></i>
                  Débloquer
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- Modal de confirmation de déblocage -->
  <ModalComp
    v-model:isVisible="showUnblockModal"
    title="Confirmer le déblocage"
    size="sm"
    cancelText="Annuler"
    confirmText="Débloquer"
    @confirm="confirmUnblock"
  >
    <template #body>
      <div class="text-black">
        <p class="mb-4">Êtes-vous sûr de vouloir débloquer l'acheteur :</p>
        <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div class="font-semibold text-black">{{ buyerToUnblock?.acheteur?.nomSociete || 'N/A' }}</div>
          <div class="text-sm text-black mt-1">{{ buyerToUnblock?.acheteur?.email || '' }}</div>
        </div>
        <p class="mt-4 text-sm text-black">
          <i class="fas fa-info-circle mr-1"></i>
          Cet acheteur pourra à nouveau commander chez vous.
        </p>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { PageHeader, BuyerAutocompleteForBlocking, ModalComp } from '@/components'
import { getBlockedBuyers, blockBuyer, unblockBuyer } from '@/api'
import { useNotification } from '@/composables/useNotification'
import type { BlockedBuyer } from '@/types'
import {useRouter} from "vue-router";
import {useAuthStore} from "@/stores/auth";

// Notifications
const { success: showSuccess, error: showError } = useNotification()

// État global
const blockedBuyers = ref<BlockedBuyer[]>([])
const selectedBuyerIds = ref<number[]>([])
const buyerAutocompleteRef = ref<{ clearSelection: () => void } | null>(null)

// Modal de confirmation
const showUnblockModal = ref(false)
const buyerToUnblock = ref<BlockedBuyer | null>(null)
const authStore = useAuthStore()
const router = useRouter()
// Méthodes
const loadBlockedBuyers = async () => {
  try {
    const data = await getBlockedBuyers()
    blockedBuyers.value = data
  } catch (error) {
    showError('Erreur lors du chargement des acheteurs bloqués')
  }
}

const handleBlockBuyer = async () => {
  if (!selectedBuyerIds.value.length) {
    showError('Veuillez sélectionner au moins un acheteur dans la liste')
    return
  }

  try {
    // Bloquer en série pour conserver les messages d'erreur potentiels par acheteur
    for (const id of selectedBuyerIds.value) {
      await blockBuyer(id)
    }

    // Réinitialiser le formulaire
    selectedBuyerIds.value = []
    buyerAutocompleteRef.value?.clearSelection()

    // Recharger la liste
    await loadBlockedBuyers()

    showSuccess('Acheteur(s) bloqué(s) avec succès')
  } catch (error) {
    showError('Erreur lors du blocage des acheteurs')
  }
}

const handleUnblockBuyer = (buyer: BlockedBuyer) => {
  buyerToUnblock.value = buyer
  showUnblockModal.value = true
}

const confirmUnblock = async () => {
  if (!buyerToUnblock.value) return
  
  try {
    await unblockBuyer(buyerToUnblock.value.id_vendeur_acheteur_disabled)
    await loadBlockedBuyers()
    showSuccess('Acheteur débloqué avec succès')
    buyerToUnblock.value = null
  } catch (error) {
    showError('Erreur lors du déblocage de l\'acheteur')
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  await loadBlockedBuyers()
})
</script>

<style lang="scss" scoped></style>
